/**
 * Décisions pures autour de la connexion d'un intervenant à la room LiveKit : quand considérer
 * qu'elle a échoué, comment qualifier l'erreur, quel navigateur prévenir avant d'essayer.
 * `LiveKitRoom` observe et affiche, la règle vit ici pour être testée sans room.
 */
import { ConnectionError, ConnectionErrorReason, DisconnectReason } from 'livekit-client';

/** Au-delà, un état « en préparation » sans room connectée devient un échec actionnable. */
export const ROOM_CONNECT_TIMEOUT_MS = 20_000;

export type RoomFailureKind =
  | 'unreachable'
  | 'timeout'
  | 'unauthorized'
  | 'negotiation'
  | 'duplicate'
  | 'removed'
  | 'unknown';

export type RoomFailure = {
  kind: RoomFailureKind;
  message: string;
};

const BENIGN_MESSAGES = [
  'Client initiated disconnect',
  'Connection attempt aborted',
  'Signal connection aborted',
];

/** Erreurs que LiveKit émet lors d'une déconnexion voulue ou d'un remontage : rien à montrer. */
export function isBenignRoomError(error: Error): boolean {
  const message = error.message ?? '';
  if (BENIGN_MESSAGES.some((benign) => message.includes(benign))) return true;
  return (
    error instanceof ConnectionError &&
    (error.reason === ConnectionErrorReason.Cancelled ||
      error.reason === ConnectionErrorReason.LeaveRequest)
  );
}

/** `null` quand l'erreur ne mérite pas d'écran, sinon la nature de l'échec à afficher et remonter. */
export function classifyRoomError(error: Error): RoomFailure | null {
  if (isBenignRoomError(error)) return null;
  const message = error.message ?? error.name;

  if (error instanceof ConnectionError) {
    switch (error.reason) {
      case ConnectionErrorReason.NotAllowed:
        return { kind: 'unauthorized', message };
      case ConnectionErrorReason.Timeout:
        return { kind: 'timeout', message };
      case ConnectionErrorReason.ServerUnreachable:
      case ConnectionErrorReason.WebSocket:
      case ConnectionErrorReason.ServiceNotFound:
        return { kind: 'unreachable', message };
      default:
        break;
    }
  }

  if (error.name === 'NegotiationError' || /negotiation/i.test(message)) {
    return { kind: 'negotiation', message };
  }
  if (/signal connection|Failed to fetch|WebSocket/i.test(message)) {
    return { kind: 'unreachable', message };
  }
  return { kind: 'unknown', message };
}

export type RoomConnectionState = 'connecting' | 'connected' | 'reconnecting' | 'disconnected';

/**
 * Une room qui n'est toujours pas connectée après le délai a échoué, même sans erreur émise.
 * Une reprise en cours (`reconnecting`) appartient au SDK : il émet `Disconnected` s'il renonce.
 * Le 22/09 sur Android, déclarer l'échec pendant sa reprise cassait une session déjà rétablie.
 */
export function hasRoomConnectionTimedOut(
  attemptStartedAtMs: number,
  nowMs: number,
  state: RoomConnectionState,
  timeoutMs: number = ROOM_CONNECT_TIMEOUT_MS,
): boolean {
  if (state === 'connected' || state === 'reconnecting') return false;
  return nowMs - attemptStartedAtMs >= timeoutMs;
}

/** Délai entre deux reconnexions automatiques après une perte de room. */
export const ROOM_AUTO_RETRY_DELAY_MS = 5_000;

export type DisconnectDecision = {
  failure: RoomFailure | null;
  autoRetry: boolean;
  /** La page a été gelée par le navigateur : reprendre au retour au premier plan, sans écran. */
  resumeOnForeground: boolean;
};

/**
 * Après une déconnexion signalée par la room, décide quoi montrer et s'il faut retenter seul.
 * Le doublon d'identité (même lien ouvert deux fois) ne doit jamais retenter : chaque reconnexion
 * éjecte l'autre appareil, qui reconnecte à son tour, sans fin (160 sessions sur une invitée le 13/09).
 * Un `CLIENT_INITIATED` que la page n'a pas demandé vient du SDK lui-même, qui se déconnecte quand
 * le navigateur gèle l'onglet (invitée hors scène, téléphone verrouillé) : à reprendre au réveil.
 */
export function decideAfterDisconnect(
  reason: DisconnectReason | undefined,
  intentional = false,
): DisconnectDecision {
  if (intentional) return { failure: null, autoRetry: false, resumeOnForeground: false };
  switch (reason) {
    case DisconnectReason.CLIENT_INITIATED:
      return { failure: null, autoRetry: false, resumeOnForeground: true };
    case DisconnectReason.DUPLICATE_IDENTITY:
      return {
        failure: { kind: 'duplicate', message: 'DUPLICATE_IDENTITY' },
        autoRetry: false,
        resumeOnForeground: false,
      };
    case DisconnectReason.PARTICIPANT_REMOVED:
    case DisconnectReason.ROOM_DELETED:
      return {
        failure: { kind: 'removed', message: DisconnectReason[reason] },
        autoRetry: false,
        resumeOnForeground: false,
      };
    case DisconnectReason.CONNECTION_TIMEOUT:
      return {
        failure: { kind: 'timeout', message: 'CONNECTION_TIMEOUT' },
        autoRetry: true,
        resumeOnForeground: false,
      };
    default:
      return {
        failure: {
          kind: 'unreachable',
          message: reason == null ? 'unknown' : DisconnectReason[reason],
        },
        autoRetry: true,
        resumeOnForeground: false,
      };
  }
}

export type ForegroundReturnAction = 'none' | 'wait' | 'reconnect';

/**
 * Au retour de la page au premier plan : une room connectée n'a rien à faire, une reprise du SDK
 * en cours se laisse finir (le chien de garde repart de zéro), une room tombée pendant le sommeil
 * se reconnecte tout de suite sans attendre le délai de retry, sauf échec définitif.
 */
export function decideOnForegroundReturn(
  state: RoomConnectionState,
  lastDisconnect: DisconnectDecision | null,
  failure: RoomFailure | null,
): ForegroundReturnAction {
  if (state === 'connected') return 'none';
  if (state === 'connecting' || state === 'reconnecting') return 'wait';
  if (failure) return shouldAutoRetry(failure) ? 'reconnect' : 'none';
  return lastDisconnect?.resumeOnForeground ? 'reconnect' : 'none';
}

/** Un échec de connexion initiale se retente seul sauf s'il est définitif (jeton, doublon, retrait). */
export function shouldAutoRetry(failure: RoomFailure): boolean {
  return (
    failure.kind !== 'unauthorized' && failure.kind !== 'duplicate' && failure.kind !== 'removed'
  );
}
